import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageForm from '../../templates/PageForm';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { toast } from 'react-toastify';
import appConfig from '../../../config/app-config';

const { address: RECOMMENDATION_ENGINE_URL, key: API_KEY } = appConfig.apis.recommendationEngineApiEnv;
const BASE_URL = `${RECOMMENDATION_ENGINE_URL}/rules`;

const RecommendationEnginePageView: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(true);
    const [rule, setRule] = useState<any>(null);

    useEffect(() => {
        if (id) {
            loadRule();
        }
    }, [id]);

    const loadRule = async () => {
        setLoading(true);
        try {
            const authToken = localStorage.getItem('authToken');
            const response = await fetch(`${BASE_URL}/${id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY,
                    'Authorization': `Bearer ${authToken}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setRule(data.rule);
            } else {
                toast.error('Erro ao carregar regra');
                navigate('/recommendation-engine/rules/0/20/name/ASC');
            }
        } catch (error) {
            console.error('Erro ao carregar regra:', error);
            toast.error('Erro ao carregar regra');
            navigate('/recommendation-engine/rules/0/20/name/ASC');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate('/recommendation-engine/rules/0/20/name/ASC');
    };

    const getStatusBadge = (status: string) => {
        const baseClasses = "px-3 py-1 rounded-full text-sm font-medium";
        if (status === 'ACTIVE') {
            return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
        }
        return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
    };

    const getTypeBadge = (type: string) => {
        const baseClasses = "px-3 py-1 rounded-full text-sm font-medium";
        if (type === 'COMPLIANCE') {
            return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
        }
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
    };

    const getRecommendationBadge = (recommendation: string) => {
        const colors: Record<string, string> = {
            'APPROVED': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
            'REJECTED': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
            'PENDING': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
            'CREDIT_FLAG': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
        };

        const labels: Record<string, string> = {
            'APPROVED': 'Aprovado',
            'REJECTED': 'Reprovado',
            'PENDING': 'Pendente',
            'CREDIT_FLAG': 'Sinalizar Crédito'
        };

        return {
            className: `px-3 py-1 rounded-full text-sm font-medium ${colors[recommendation] || colors['PENDING']}`,
            label: labels[recommendation] || recommendation
        };
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-slate-800 flex items-center justify-center">
                <div className="text-gray-600 dark:text-gray-300">Carregando...</div>
            </div>
        );
    }

    if (!rule) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-slate-800 flex items-center justify-center">
                <div className="text-gray-600 dark:text-gray-300">Regra não encontrada</div>
            </div>
        );
    }

    return (
        <PageForm
            pageTitle={rule.name}
            apiVersion="v1.0"
            isLoading={loading}
            breadcrumb={[
                { label: 'Regras', url: '/recommendation-engine/rules/0/20/name/ASC' },
                { label: 'Visualizar Regra', url: '#' }
            ]}
            controlSlot={
                <div className="flex justify-end">
                    <button
                        className="text-black bg-gray-300 hover:bg-gray-400 px-8 py-2.5 rounded"
                        onClick={handleBack}
                    >
                        <IoMdArrowRoundBack />
                    </button>
                </div>
            }
        >
            <div className="bg-white dark:bg-slate-700 rounded-lg shadow">
                <div className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Informações Básicas */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                Informações Básicas
                            </h3>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Nome da Regra
                                </label>
                                <p className="text-gray-900 dark:text-gray-100">{rule.name}</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Descrição
                                </label>
                                <p className="text-gray-900 dark:text-gray-100">{rule.description}</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Tipo
                                    </label>
                                    <span className={getTypeBadge(rule.context)}>
                                        {rule.context === 'PLD' ? 'PLD' : 'Crédito'}
                                    </span>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Status
                                    </label>
                                    <span className={getStatusBadge(rule.active ? 'ACTIVE' : 'INACTIVE')}>
                                        {rule.active ? 'Ativo' : 'Inativo'}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Documento
                                </label>
                                <p className="text-gray-900 dark:text-gray-100">
                                    {rule.person_type === 'BOTH' ? 'CPF/CNPJ' : rule.person_type}
                                </p>
                            </div>
                        </div>

                        {/* Metadados */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                Metadados
                            </h3>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Criado por
                                </label>
                                <p className="text-gray-900 dark:text-gray-100">{rule.created_by}</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Data de Criação
                                </label>
                                <p className="text-gray-900 dark:text-gray-100">
                                    {new Date(rule.created_at).toLocaleString('pt-BR')}
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Última Atualização
                                </label>
                                <p className="text-gray-900 dark:text-gray-100">
                                    {new Date(rule.updated_at).toLocaleString('pt-BR')}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Configurações da Regra */}
                    <div className="mt-8 space-y-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            Configurações da Regra
                        </h3>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Birô
                                </label>
                                <p className="text-gray-900 dark:text-gray-100">{rule.provider}</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Recomendação
                                </label>
                                <span className={getRecommendationBadge(rule.recommendation).className}>
                                    {getRecommendationBadge(rule.recommendation).label}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </PageForm>
    );
};

export default RecommendationEnginePageView;
